/**
 * State initialization helpers for CardBody component
 * Contains functions to initialize edit state from group/row data
 */

import moment from "moment-timezone";
import TimestampConverter from "../helpers/TimestampConverter";
import zoneConverter from "../helpers/zoneConverter";
import { convertTo24HourFormatback } from "../services/timeFormat";
import { formatTime } from "../services/trackerServices";

/**
 * Initialize edit state from a group or row object
 * This pattern is repeated many times throughout CardBody
 */
export const initializeEditStateFromGroup = ({
  group,
  effectiveSetEditedId,
  setProjectEditDropdownOpen,
  setTagEditDropdownOpen,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintNameEdit,
  setSprintIdEdit,
  setDateEdit,
  setTagIdEdit,
  setTagEdit,
  setTagQuery,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setUFormatedTime,
  setUDuration,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  globalConfig,
  useMomentFormat = false,
}) => {
  effectiveSetEditedId(group._id);
  closeAllDropdowns(setProjectEditDropdownOpen, setTagEditDropdownOpen);
  setTaskNameEdit(group.task);
  setProjectIdEdit(group.project._id);
  setProjectNameEdit(group.project.project_name);
  
  if (group.sprint) {
    setSprintNameEdit(group.sprint.sprint_name);
    setSprintIdEdit(group.sprint._id);
  } else {
    setSprintIdEdit("");
    setSprintNameEdit("");
  }
  
  // Handle date formatting - some places use moment.tz, others use zoneConverter
  if (useMomentFormat) {
    setDateEdit(
      moment.tz(group.date, "YYYY-MM-DD HH:mm:ss", globalConfig.getTimeZone()).format()
    );
  } else {
    setDateEdit(
      zoneConverter(group.date, globalConfig.getTimeZone(), "YYYY-MM-DD HH:mm:ss")
    );
  }
  
  setTagIdEdit(group.tag && group.tag._id);
  setTagEdit(group.tag && group.tag.tag_name);
  setTagQuery("");
  
  setStartTimeEdit(TimestampConverter(group.startTime));
  setEndTimeEdit(TimestampConverter(group.endTime));
  setStartTimeTemp(TimestampConverter(group.startTime));
  setEndTimeTemp(TimestampConverter(group.endTime));
  setStartTimeEdit24(convertTo24HourFormatback(TimestampConverter(group.startTime)));
  setEndTimeEdit24(convertTo24HourFormatback(TimestampConverter(group.endTime)));
  
  setClientEdit(group.client);
  setClientIdEdit(group.clientId);
  setBillableEdit(group.billable);
  
  // Handle both group (with totalDuration) and row (with duration) objects
  const duration = group.totalDuration || group.duration || 0;
  setUFormatedTime(formatDurationFromSeconds(duration));
  setUDuration(duration);
  clearTimeErrors(setUStartTimeError, setUEndTimeError, setUFormatedTimeError);
};

/**
 * Format duration from seconds to HH:MM:SS format
 * @param {number} totalSeconds - Total seconds to format
 * @returns {string} Formatted time string (HH:MM:SS)
 */
export const formatDurationFromSeconds = (totalSeconds) => {
  const duration = totalSeconds || 0;
  return `${formatTime(Math.floor(duration / 3600))}:${formatTime(
    Math.floor(parseInt((duration % 3600) / 60))
  )}:${formatTime(Math.floor(duration % 60))}`;
};

/**
 * Clear all time-related error states
 * @param {Function} setUStartTimeError - Setter for start time error
 * @param {Function} setUEndTimeError - Setter for end time error
 * @param {Function} setUFormatedTimeError - Setter for formatted time error
 */
export const clearTimeErrors = (setUStartTimeError, setUEndTimeError, setUFormatedTimeError) => {
  setUStartTimeError(false);
  setUEndTimeError(false);
  setUFormatedTimeError(false);
};

/**
 * Close all dropdown menus
 * @param {Function} setProjectEditDropdownOpen - Setter for project dropdown
 * @param {Function} setTagEditDropdownOpen - Setter for tag dropdown
 */
export const closeAllDropdowns = (setProjectEditDropdownOpen, setTagEditDropdownOpen) => {
  setProjectEditDropdownOpen(false);
  setTagEditDropdownOpen(false);
};
